import { Mode } from 'src/@types/mode';
import { OrgType } from 'src/@types/org';
import { Role } from 'src/@types/role';
import { UserWithHmacType } from 'src/@types/user';
import { DT_MIXPANEL_TOKEN, MIXPANEL_TOKEN } from 'src/config';

type Mixpanel = typeof import('mixpanel-browser').default;

let userIsNuminarAdmin: boolean = false;
let orgMode: Mode = 'numinar';

const OPTIONS = {
  autocapture: false,
  stop_utm_persistence: true,
  record_sessions_percent: 0,
  record_idle_timeout_ms: 300000, // 5 min of inactivity
  record_heatmap_data: true,
};

/**
 * Mixpanel is around 850 kB and nothing on screen waits for it, so it is fetched when the first
 * event is sent rather than with the app. Both projects are configured the one time the module
 * arrives, which is why the tokens are named here and nowhere else.
 */
type Projects = { numinar: Mixpanel; datatrust: Mixpanel };

let loading: Promise<Projects | null> | null = null;

const load = () =>
  (loading ??= import('mixpanel-browser')
    .then(({ default: numinar }) => {
      numinar.init(MIXPANEL_TOKEN, OPTIONS);
      const datatrust = numinar.init(DT_MIXPANEL_TOKEN, OPTIONS, 'datatrust') as Mixpanel;
      return { numinar, datatrust };
    })
    .catch((error) => {
      // A rejected promise memoizes as readily as a resolved one, so a chunk that 404s across a
      // deploy would end analytics for the tab and add an unhandled rejection per event. Clearing
      // the memo lets the next event refetch, and resolving to null keeps the failure quiet.
      loading = null;
      console.error(error);
      return null;
    }));

/**
 * Call sites stay synchronous, and their events arrive in the order they were sent, because every
 * one of them queues behind the same load. An event sent in the last moments before the tab closes
 * can be lost, which is the cost of not shipping the library on the critical path.
 */
const send = (fn: (projects: Projects) => void) => {
  void load().then((projects) => {
    if (projects) fn(projects);
  });
};

export function setMixpanelUser(
  currentUser: UserWithHmacType,
  userRole?: Role,
  currentOrg?: OrgType
) {
  userIsNuminarAdmin = userRole === Role.NuminarAdmin;

  const userProperties = {
    $email: currentUser?.email,
    $name: currentUser?.first_name + ' ' + currentUser?.last_name,
    $avatar: currentUser?.profile_url,
    role: userRole,
  };

  // Read now rather than when the send runs, so a later role or org change cannot reroute an
  // event that was already decided.
  const toDatatrust = !!currentOrg?.managed_by_datatrust && !userIsNuminarAdmin;

  send(({ numinar, datatrust }) => {
    if (toDatatrust) {
      datatrust.identify(currentUser.id);
      datatrust.people.set(userProperties);
    }

    numinar.identify(currentUser.id);
    numinar.people.set(userProperties);
  });
}

export function setMixpanelOrg(currentOrg: OrgType) {
  orgMode = currentOrg.managed_by_datatrust ? 'dt' : 'numinar';

  const orgProperties = {
    $name: currentOrg.name,
    'Managed by DT': currentOrg.managed_by_datatrust,
    'Data Source': currentOrg.data_source,
    'Can Call': currentOrg.can_call,
    'Can Text': currentOrg.can_text,
    'Can Canvass': currentOrg.can_canvass,
    'Can Poll': currentOrg.can_poll,
    'Can Custom Model': currentOrg.can_custom_model,
    'Can Access Canvassing QA': currentOrg.can_access_canvassing_qa,
    'Has Relational Enabled': currentOrg.has_relational_enabled,
    'Has Paid Relational Enabled': currentOrg.has_paid_relational_enabled,
  };

  const userProperties = {
    $org_id: currentOrg?.id,
    'Current Organziation': currentOrg.name,
  };

  const toDatatrust = currentOrg.managed_by_datatrust && !userIsNuminarAdmin;

  send(({ numinar, datatrust }) => {
    if (toDatatrust) {
      datatrust.set_group('org_id', currentOrg.id);
      datatrust.get_group('org_id', currentOrg.id).set(orgProperties);
      datatrust.people.set(userProperties);
    }

    numinar.set_group('org_id', currentOrg.id);
    numinar.get_group('org_id', currentOrg.id).set(orgProperties);
    numinar.people.set(userProperties);
  });
}

export function mixpanelTrack(eventName: string, properties: object = {}) {
  const toDatatrust = orgMode === 'dt' && !userIsNuminarAdmin;

  send(({ numinar, datatrust }) => {
    if (toDatatrust) {
      datatrust.track(eventName, properties);
    }
    numinar.track(eventName, properties);
  });
}

/** Clears the identity on sign-out and on losing access to the org that was being measured. */
export function resetMixpanel() {
  send(({ numinar }) => numinar.reset());
}
